import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Breadcrumbs,
  Link,
  Chip,
  Grid,
  Fab,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useNavigate, useParams } from 'react-router-dom';
import BuyInHistory from '../../components/BuyInHistory/BuyInHistory';
import CashOutHistory from '../../components/CashOutHistory/CashOutHistory';
import AddBuyInDialog from '../../components/AddBuyInDialog/AddBuyInDialog';
import AddCashOutDialog from '../../components/AddCashOutDialog/AddCashOutDialog';
import CSVDownloadButton from '../../components/CSVDownloadButton/CSVDownloadButton';
import { usePlayer } from '../../context/PlayerContext';
import { useCSVDownload } from '../../hooks/useCSVDownload';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import './PlayerDetails.scss';

const PlayerDetails = ({ sessionName = null }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getPlayerById, editPlayer, deleteBuyIn, deleteCashOut } = usePlayer();
  const { downloadPlayerHistoryCSV } = useCSVDownload(sessionName);
  const [addBuyInOpen, setAddBuyInOpen] = useState(false);
  const [addCashOutOpen, setAddCashOutOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('buyins');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editError, setEditError] = useState('');
  
  const player = getPlayerById(id);

  if (!player) {
    return (
      <Box className="player-not-found">
        <Typography variant="h4">Player Not Found</Typography>
        <Typography variant="body1" color="text.secondary">
          The player you're looking for doesn't exist.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getAverageBuyIn = () => {
    if (!player.buyInHistory || player.buyInHistory.length === 0) return 0;
    return player.totalBuyIn / player.buyInHistory.length;
  };

  const getAverageCashOut = () => {
    const cashOuts = player.cashOutHistory || [];
    if (cashOuts.length === 0) return 0;
    return (player.totalCashOut || 0) / cashOuts.length;
  };

  const getNetProfitLoss = () => {
    return (player.totalCashOut || 0) - player.totalBuyIn;
  };

  const isProfit = () => {
    return getNetProfitLoss() >= 0;
  };

  const getLastBuyInDate = () => {
    if (!player.buyInHistory || player.buyInHistory.length === 0) return null;
    const lastBuyIn = player.buyInHistory[player.buyInHistory.length - 1];
    return new Date(lastBuyIn.timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownloadHistory = () => {
    downloadPlayerHistoryCSV(player);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditPlayer = () => {
    setEditedName(player.name);
    setEditError('');
    setEditDialogOpen(true);
  };

  const handleEditSubmit = () => {
    try {
      editPlayer(player.id, editedName);
      setEditDialogOpen(false);
      setEditedName('');
      setEditError('');
    } catch (error) {
      setEditError(error.message);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditedName('');
    setEditError('');
  };

  const playerInitials = getInitials(player.name);
  const backgroundColor = player.profileColor || "#006aff";
  const textColor = getContrastTextColor(backgroundColor);

  return (
    <Box className="player-details fade-in">
      {/* Navigation */}
      <Box className="navigation">
        <Breadcrumbs aria-label="breadcrumb" className="breadcrumbs">
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/')}
            className="breadcrumb-link"
          >
            Dashboard
          </Link>
          <Typography variant="body2" color="text.primary">
            {player.name}
          </Typography>
        </Breadcrumbs>
        
        <IconButton
          onClick={() => navigate('/')}
          className="back-button"
          aria-label="go back"
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      {/* Player Header */}
      <Card className="player-header-card">
        <CardContent>
          <Box className="player-header">
            <Box className="player-info">
              <Box 
                className="player-avatar"
                style={{
                  backgroundColor: backgroundColor,
                  color: textColor
                }}
              >
                {playerInitials}
              </Box>
              <Box>
                <Box className="player-name-section">
                  <Typography variant="h3" className="player-name">
                    {player.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleEditPlayer}
                    className="edit-name-button"
                    aria-label="edit player name"
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
                <Box className="player-meta">
                  <Chip 
                    label={`${player.buyInHistory?.length || 0} buy-ins`} 
                    size="small" 
                    variant="outlined"
                  />
                  <Chip 
                    label={`${player.cashOutHistory?.length || 0} cash outs`} 
                    size="small" 
                    variant="outlined"
                    color="success"
                  />
                  {getLastBuyInDate() && (
                    <Typography variant="body2" color="text.secondary">
                      Last activity: {getLastBuyInDate()}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            
            <Box className="header-actions">
              <CSVDownloadButton
                onClick={handleDownloadHistory}
                disabled={!player.buyInHistory || player.buyInHistory.length === 0}
                tooltip={!player.buyInHistory || player.buyInHistory.length === 0 ? "No history to download" : "Download transaction history as CSV"}
                label="Export History"
                size="medium"
                className="export-history-button desktop-only"
              />
              <Button
                variant="outlined"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={() => setAddCashOutOpen(true)}
                className="cash-out-button desktop-only"
                color="success"
              >
                Cash Out
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddBuyInOpen(true)}
                className="add-buyin-button desktop-only"
              >
                Add Buy-in
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Grid container spacing={3} className="player-stats">
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stat-card">
            <CardContent>
              <Box className="stat-content">
                <Box className="currency-icon blue">$</Box>
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(player.totalBuyIn).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Buy-ins
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stat-card success">
            <CardContent>
              <Box className="stat-content">
                <Box className="currency-icon green">$</Box>
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(player.totalCashOut || 0).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Cash Outs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={`stat-card ${isProfit() ? 'profit' : 'loss'}`}>
            <CardContent>
              <Box className="stat-content">
                {isProfit() ? (
                  <TrendingUpIcon className="stat-icon profit" />
                ) : (
                  <TrendingDownIcon className="stat-icon loss" />
                )}
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(Math.abs(getNetProfitLoss())).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net {isProfit() ? 'Profit' : 'Loss'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stat-card">
            <CardContent>
              <Box className="stat-content">
                <Box className="count-icon">#</Box>
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {(player.buyInHistory?.length || 0) + (player.cashOutHistory?.length || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Transactions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* History Tabs */}
      <Box className="history-section">
        <Box className="history-tabs">
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            className="tabs"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab 
              label={`Buy-ins (${player.buyInHistory?.length || 0})`}
              value="buyins"
            />
            <Tab 
              label={`Cash Outs (${player.cashOutHistory?.length || 0})`}
              value="cashouts"
            />
          </Tabs>
        </Box>
        
        {activeTab === 'buyins' && (
          <BuyInHistory 
            buyInHistory={player.buyInHistory || []} 
            playerId={player.id}
            onDeleteBuyIn={deleteBuyIn}
          />
        )}
        
        {activeTab === 'cashouts' && (
          <CashOutHistory 
            cashOutHistory={player.cashOutHistory || []} 
            playerId={player.id}
            onDeleteCashOut={deleteCashOut}
          />
        )}
      </Box>

      {/* Floating Action Buttons for Mobile */}
      <Fab
        color="primary"
        aria-label="add buy-in"
        className="fab primary mobile-only"
        onClick={() => setAddBuyInOpen(true)}
      >
        <AddIcon />
      </Fab>
      
      <Fab
        color="success"
        aria-label="cash out"
        className="fab secondary mobile-only"
        onClick={() => setAddCashOutOpen(true)}
      >
        <AccountBalanceWalletIcon />
      </Fab>

      {/* Edit Player Name Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleEditCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            <Typography variant="h6">
              Edit Player Name
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Player Name"
            variant="outlined"
            value={editedName}
            onChange={(e) => {
              setEditedName(e.target.value);
              if (editError) setEditError('');
            }}
            error={!!editError}
            helperText={editError}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained"
            disabled={!editedName.trim()}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <AddBuyInDialog
        open={addBuyInOpen}
        onClose={() => setAddBuyInOpen(false)}
        playerId={id}
      />
      
      <AddCashOutDialog
        open={addCashOutOpen}
        onClose={() => setAddCashOutOpen(false)}
        playerId={id}
      />
    </Box>
  );
};

export default PlayerDetails;